import { useEffect } from "react"
import parse from "html-react-parser"
import HCRender from "aldoo-ra/HelpCenter/hc-render"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { updateCDNUrls } from "aldoo-ra/HtmlEditor/cdn-urls"
import { BookOpenIcon } from "@heroicons/react/24/outline"

// An article may be stored as a whole HTML document; only its body belongs
// in the page (a nested <html>/<head> is invalid markup and breaks hydration)
const articleBody = (html) => {
  const body = html.match(/<body[^>]*>([\s\S]*)<\/body>/i)
  if (body) return body[1]
  return html.replace(/<!DOCTYPE[^>]*>|<\/?html[^>]*>|<head[^>]*>[\s\S]*?<\/head>/gi, "")
}

const calcReadTime = (html) => {
  if (!html) return null
  const text = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
  const words = text.split(" ").filter(Boolean).length
  const minutes = Math.max(1, Math.ceil(words / 200))
  return `${minutes} min`
}

const ArticleContent = ({ article, onFeedback }) => {
  const texts = TextBundle({ id: "help-center" })
  const ArticleFeedback = HCRender.getFragment("ArticleFeedback")

  if (!article) return null

  const formatDate = (dateString) => {
    if (!dateString) return ""
    // UTC: the server and the browser print the same date
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    })
  }

  const title = article.content.name || texts?.untitled_article
  const lastUpdated = article.lastUpdated || article.updatedAt || article.createdAt
  const readTime = calcReadTime(article.content?.content)

  useEffect(() => {
    const prevTitle = document.title
    document.title = `${title} - ${texts?.help_center}`
    return () => { document.title = prevTitle }
  }, [title])

  const getContent = () => {
    if (article.content && typeof article.content.content === "string") {
      const currentSlug = article.content.slug
      // Strip self-references: an article that links to itself becomes
      // styled text instead — the link is dropped.
      const replace = (node) => {
        if (node.type !== "tag" || node.name !== "a") return undefined
        const href = node.attribs?.href || ""
        const isSelf =
          currentSlug && (
            href === `/help-center/article/${currentSlug}` ||
            href.endsWith(`/article/${currentSlug}`) ||
            (typeof window !== "undefined" && href === window.location.pathname)
          )
        if (!isSelf) return undefined
        const textChildren = (node.children || [])
          .map((c) => (c.type === "text" ? c.data : ""))
          .join("")
        return <strong className="text-text">{textChildren}</strong>
      }
      return parse(articleBody(updateCDNUrls(article.content.content)), { replace })
    }
    return (
      <Typography variant="body1">
        {texts?.empty_article}
      </Typography>
    )
  }

  return (
    <article className="hc">
      <div className="flex flex-col gap-5 pb-4">
        <Typography variant="h2" component="h1" className="my-0">{title}</Typography>

        {readTime && (
          <div className="flex items-center gap-1 w-fit paper_20 rounded-full px-3 h-8">
            <BookOpenIcon className="w-4 h-4 text-text" />
            <Typography variant="txt1_accent" className="text-text">{readTime}</Typography>
          </div>
        )}

        {lastUpdated && (
          <div className="flex items-center text-text_secondary mb-4">
            <Typography variant="txt1_dim" className="mr-1 my-0">
              {texts?.update_date}:
            </Typography>
            <time dateTime={lastUpdated}>{formatDate(lastUpdated)}</time>
          </div>
        )}
      </div>

      <div className="article_content max-w-[860px]">{getContent()}</div>

      {ArticleFeedback && <ArticleFeedback article={article} onFeedback={onFeedback} />}
    </article>
  )
}

export default ArticleContent
